import { monaco } from "../monaco.ts";
import { applyWorkspaceEdit, planWorkspaceEdit, WorkspaceEditRejected, type LspBinding } from "./binding.ts";
import { completionKind, lspSeverity, markdownOf, toLspPosition, toLspRange, toMarkers, toMonacoRange } from "./convert.ts";
import { relativeFromSynthetic, syntheticUri, type LspRange } from "./types.ts";

const OWNER = "racler-lsp";
const LANGUAGES = ["typescript", "javascript"];
const bindings = new Map<string, LspBinding>();
let registered = false;

const keyOf = (machineId: string, projectId: string) => `${machineId}/${projectId}`;
/** Model URI scheme used by useOpenFiles: racler://<machineId>/<projectId>/<path>. */
export const modelUri = (machineId: string, projectId: string, path: string) =>
  monaco.Uri.from({ scheme: "racler", authority: machineId, path: `/${projectId}/${path}` });

/** The binding + Project-relative path of a model, or null when the model does not belong to a bound Project. */
function locate(model: monaco.editor.ITextModel | monaco.Uri): { b: LspBinding; path: string } | null {
  const uri = "uri" in model ? model.uri : model;
  if (uri.scheme !== "racler") return null;
  const b = [...bindings.values()].find((x) => x.machineId === uri.authority && uri.path.startsWith(`/${x.projectId}/`));
  return b ? { b, path: uri.path.slice(b.projectId.length + 2) } : null;
}

const signalOf = (token: monaco.CancellationToken): AbortSignal => {
  const c = new AbortController();
  token.onCancellationRequested(() => c.abort());
  return c.signal;
};

async function call<T>(model: monaco.editor.ITextModel, token: monaco.CancellationToken, method: string, extra: object): Promise<T | null> {
  const at = locate(model);
  if (!at) {
    return null;
  }
  try {
    return await at.b.client.request<T>(
      method,
      { textDocument: { uri: syntheticUri(at.b.projectId, at.path) }, ...extra },
      signalOf(token),
    );
  } catch {
    return null; // cancelled / timed out / server gone: Monaco simply shows nothing
  }
}

async function locationsOf(b: LspBinding, result: unknown): Promise<monaco.languages.Location[]> {
  const list = Array.isArray(result) ? result : result ? [result] : [];
  const out: monaco.languages.Location[] = [];
  for (const l of list as Array<Record<string, unknown>>) {
    const uri = (l["targetUri"] ?? l["uri"]) as string | undefined;
    const range = (l["targetSelectionRange"] ?? l["range"]) as LspRange | undefined;
    const path = uri ? relativeFromSynthetic(b.projectId, uri) : null;
    if (path === null || !range) continue; // outside the Project (e.g. lib.d.ts): never navigable, never leaked
    out.push({ uri: modelUri(b.machineId, b.projectId, path), range: toMonacoRange(range) });
  }
  await b.ensureModels([...new Set(out.map((l) => locate(l.uri)?.path).filter((p): p is string => !!p))]);
  return out;
}

function editToMonaco(b: LspBinding, edit: Parameters<typeof planWorkspaceEdit>[1]): monaco.languages.WorkspaceEdit {
  const edits: monaco.languages.IWorkspaceTextEdit[] = [];
  for (const [path, list] of planWorkspaceEdit(b.projectId, edit)) {
    for (const e of list) {
      edits.push({
        resource: modelUri(b.machineId, b.projectId, path),
        textEdit: { range: toMonacoRange(e.range), text: e.newText },
        versionId: undefined,
      });
    }
  }
  return { edits };
}

interface RawItem {
  label: string | { label: string; detail?: string; description?: string };
  kind?: number;
  detail?: string;
  documentation?: unknown;
  sortText?: string;
  filterText?: string;
  insertText?: string;
  insertTextFormat?: number;
  preselect?: boolean;
  tags?: number[];
  deprecated?: boolean;
  textEdit?: { range?: LspRange; newText: string; insert?: LspRange; replace?: LspRange };
  additionalTextEdits?: Array<{ range: LspRange; newText: string }>;
  commitCharacters?: string[];
  [k: string]: unknown;
}
type Item = monaco.languages.CompletionItem & { _raw?: RawItem };

function completionItem(b: LspBinding, model: monaco.editor.ITextModel, pos: monaco.Position, raw: RawItem): Item {
  const word = model.getWordUntilPosition(pos);
  const defaultRange = new monaco.Range(pos.lineNumber, word.startColumn, pos.lineNumber, word.endColumn);
  const te = raw.textEdit;
  const lspRange = te?.range ?? te?.replace ?? te?.insert;
  const range = lspRange ? toMonacoRange(lspRange) : defaultRange;
  const label = typeof raw.label === "string" ? raw.label : { label: raw.label.label, detail: raw.label.detail, description: raw.label.description };
  const item: Item = {
    label,
    kind: completionKind(raw.kind),
    insertText: te?.newText ?? raw.insertText ?? (typeof raw.label === "string" ? raw.label : raw.label.label),
    insertTextRules: raw.insertTextFormat === 2 ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet : undefined,
    range,
    ...(raw.detail ? { detail: raw.detail } : {}),
    ...(raw.documentation ? { documentation: { value: markdownOf(raw.documentation) } } : {}),
    ...(raw.sortText ? { sortText: raw.sortText } : {}),
    ...(raw.filterText ? { filterText: raw.filterText } : {}),
    ...(raw.preselect ? { preselect: true } : {}),
    ...(raw.tags?.includes(1) || raw.deprecated ? { tags: [monaco.languages.CompletionItemTag.Deprecated] } : {}),
    ...(raw.commitCharacters ? { commitCharacters: raw.commitCharacters } : {}),
    ...(raw.additionalTextEdits
      ? { additionalTextEdits: raw.additionalTextEdits.map((e) => ({ range: toMonacoRange(e.range), text: e.newText })) }
      : {}),
  };
  item._raw = raw;
  void b;
  return item;
}

function register(): void {
  const L = monaco.languages;
  for (const lang of LANGUAGES) {
    L.registerCompletionItemProvider(lang, {
      // Exactly what typescript-language-server advertises: it answers an unadvertised trigger character with an empty list.
      triggerCharacters: [".", '"', "'", "/", "@", "<"],
      async provideCompletionItems(model, position, context, token) {
        const at = locate(model);
        if (!at) return { suggestions: [] };
        const res = await call<{ items?: RawItem[]; isIncomplete?: boolean } | RawItem[]>(model, token, "textDocument/completion", {
          position: toLspPosition(position),
          context: { triggerKind: context.triggerKind + 1, ...(context.triggerCharacter ? { triggerCharacter: context.triggerCharacter } : {}) },
        });
        const items = Array.isArray(res) ? res : (res?.items ?? []);
        return {
          suggestions: items.map((i) => completionItem(at.b, model, position, i)),
          incomplete: !Array.isArray(res) && res?.isIncomplete === true,
        };
      },
      async resolveCompletionItem(item, token) {
        const raw = (item as Item)._raw;
        const open = [...bindings.values()].find((b) => b.client.currentState === "ready");
        if (!raw || !open) return item;
        try {
          const r = await open.client.request<RawItem>("completionItem/resolve", raw, signalOf(token));
          if (!r) return item;
          return {
            ...item,
            ...(r.detail ? { detail: r.detail } : {}),
            ...(r.documentation ? { documentation: { value: markdownOf(r.documentation) } } : {}),
            ...(r.additionalTextEdits
              ? { additionalTextEdits: r.additionalTextEdits.map((e) => ({ range: toMonacoRange(e.range), text: e.newText })) }
              : {}),
          };
        } catch {
          return item;
        }
      },
    });

    L.registerHoverProvider(lang, {
      async provideHover(model, position, token) {
        const r = await call<{ contents: unknown; range?: LspRange }>(model, token, "textDocument/hover", { position: toLspPosition(position) });
        const text = r ? markdownOf(r.contents) : "";
        if (!r || !text) return null;
        return { contents: [{ value: text }], ...(r.range ? { range: toMonacoRange(r.range) } : {}) };
      },
    });

    L.registerSignatureHelpProvider(lang, {
      signatureHelpTriggerCharacters: ["(", ","],
      signatureHelpRetriggerCharacters: [")"],
      async provideSignatureHelp(model, position, token, context) {
        const r = await call<{ signatures: Array<{ label: string; documentation?: unknown; parameters?: Array<{ label: string | [number, number]; documentation?: unknown }>; activeParameter?: number }>; activeSignature?: number; activeParameter?: number }>(
          model,
          token,
          "textDocument/signatureHelp",
          { position: toLspPosition(position), context: { triggerKind: context.triggerKind, isRetrigger: context.isRetrigger, ...(context.triggerCharacter ? { triggerCharacter: context.triggerCharacter } : {}) } },
        );
        if (!r || r.signatures.length === 0) return null;
        return {
          value: {
            signatures: r.signatures.map((s) => ({
              label: s.label,
              ...(s.documentation ? { documentation: { value: markdownOf(s.documentation) } } : {}),
              parameters: (s.parameters ?? []).map((p) => ({ label: p.label, ...(p.documentation ? { documentation: { value: markdownOf(p.documentation) } } : {}) })),
              ...(s.activeParameter !== undefined ? { activeParameter: s.activeParameter } : {}),
            })),
            activeSignature: r.activeSignature ?? 0,
            activeParameter: r.activeParameter ?? 0,
          },
          dispose() {},
        };
      },
    });

    L.registerDefinitionProvider(lang, {
      async provideDefinition(model, position, token) {
        const at = locate(model);
        const r = await call<unknown>(model, token, "textDocument/definition", { position: toLspPosition(position) });
        return at ? await locationsOf(at.b, r) : [];
      },
    });

    L.registerReferenceProvider(lang, {
      async provideReferences(model, position, context, token) {
        const at = locate(model);
        const r = await call<unknown>(model, token, "textDocument/references", {
          position: toLspPosition(position),
          context: { includeDeclaration: context.includeDeclaration },
        });
        return at ? await locationsOf(at.b, r) : [];
      },
    });

    L.registerRenameProvider(lang, {
      async resolveRenameLocation(model, position, token) {
        // LSP allows three shapes: Range | { range, placeholder } | { defaultBehavior: true }.
        const r = await call<(Partial<LspRange> & { range?: LspRange; placeholder?: string; defaultBehavior?: boolean }) | null>(
          model,
          token,
          "textDocument/prepareRename",
          { position: toLspPosition(position) },
        );
        const lspRange: LspRange | null = r?.range ?? (r?.start && r.end ? { start: r.start, end: r.end } : null);
        if (lspRange) {
          const range = toMonacoRange(lspRange);
          return { range, text: r?.placeholder ?? model.getValueInRange(range) };
        }
        if (r?.defaultBehavior) {
          const w = model.getWordAtPosition(position);
          if (w) {
            return { range: new monaco.Range(position.lineNumber, w.startColumn, position.lineNumber, w.endColumn), text: w.word };
          }
        }
        return { range: new monaco.Range(1, 1, 1, 1), text: "", rejectReason: "You cannot rename this element." };
      },
      async provideRenameEdits(model, position, newName, token) {
        const at = locate(model);
        const r = await call<Parameters<typeof planWorkspaceEdit>[1] | null>(model, token, "textDocument/rename", { position: toLspPosition(position), newName });
        if (!at || !r) return { edits: [], rejectReason: "Rename failed." };
        try {
          // Project-bounded: planWorkspaceEdit throws for any URI outside this Project; nothing is applied then.
          const edit = editToMonaco(at.b, r);
          // Other files are opened as tabs first, so the rename lands as ordinary unsaved (undoable) edits.
          const paths = [...planWorkspaceEdit(at.b.projectId, r).keys()];
          if (!(await at.b.ensureOpen(paths))) return { edits: [], rejectReason: "Some files could not be opened; nothing was changed." };
          return edit;
        } catch (err) {
          const msg = err instanceof WorkspaceEditRejected ? err.message : "Rename failed.";
          at.b.report(msg);
          return { edits: [], rejectReason: msg };
        }
      },
    });

    L.registerCodeActionProvider(lang, {
      async provideCodeActions(model, range, context, token) {
        const at = locate(model);
        if (!at) return { actions: [], dispose() {} };
        const diagnostics = context.markers
          .map((m) => ({ range: toLspRange(m), message: m.message, severity: lspSeverity(m.severity), ...(typeof m.code === "string" ? { code: m.code } : {}), ...(m.source ? { source: m.source } : {}) }));
        const r = await call<Array<{ title: string; kind?: string; isPreferred?: boolean; edit?: Parameters<typeof planWorkspaceEdit>[1]; command?: { command: string }; diagnostics?: unknown[] }>>(model, token, "textDocument/codeAction", {
          range: toLspRange(range),
          context: { diagnostics, ...(context.only ? { only: [context.only] } : {}) },
        });
        const actions: monaco.languages.CodeAction[] = [];
        for (const a of r ?? []) {
          if (!a.edit) continue; // command-only actions need workspace/executeCommand; edit-bearing ones cover quick fixes + auto-import
          let edit: monaco.languages.WorkspaceEdit;
          try {
            edit = editToMonaco(at.b, a.edit);
          } catch {
            continue; // would touch a file outside the Project: not offered
          }
          const touched = [...planWorkspaceEdit(at.b.projectId, a.edit).keys()];
          if (!(await at.b.ensureOpen(touched))) continue;
          actions.push({ title: a.title, ...(a.kind ? { kind: a.kind } : {}), ...(a.isPreferred ? { isPreferred: true } : {}), edit });
        }
        return { actions, dispose() {} };
      },
    });
  }
}

export function markersFor(path: string, b: LspBinding): monaco.editor.IMarkerData[] {
  return toMarkers(b.client.diagnostics.forPath(path));
}

/** Binds a Project's LSP client to Monaco: providers (once), inline markers, Project-bounded edits. Returns the unbind fn. */
export function bindProject(b: LspBinding): () => void {
  if (!registered) {
    registered = true;
    register();
  }
  const key = keyOf(b.machineId, b.projectId);
  bindings.set(key, b);
  const refresh = (changed: string | null) => {
    if (changed === null) return;
    const model = monaco.editor.getModel(modelUri(b.machineId, b.projectId, changed));
    if (model) monaco.editor.setModelMarkers(model, OWNER, markersFor(changed, b));
  };
  const off = b.client.diagnostics.subscribe(refresh);
  // Go to Definition (and peek "open") of another Project file opens it as a real tab, never as a stray model.
  const opener = monaco.editor.registerEditorOpener({
    openCodeEditor(_source, resource, selection) {
      const at = locate(resource);
      if (!at || at.b !== b) return false;
      const r = selection as Partial<monaco.IRange & monaco.IPosition> | undefined;
      const range = r
        ? {
            startLineNumber: r.startLineNumber ?? r.lineNumber ?? 1,
            startColumn: r.startColumn ?? r.column ?? 1,
            endLineNumber: r.endLineNumber ?? r.startLineNumber ?? r.lineNumber ?? 1,
            endColumn: r.endColumn ?? r.startColumn ?? r.column ?? 1,
          }
        : { startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1 };
      b.reveal(at.path, range);
      return true;
    },
  });
  return () => {
    off();
    opener.dispose();
    if (bindings.get(key) === b) bindings.delete(key);
    for (const m of monaco.editor.getModels()) {
      const at = locate(m);
      if (at?.b === b) monaco.editor.setModelMarkers(m, OWNER, []);
    }
  };
}

/** Re-applies markers when a model is (re)created after its diagnostics arrived. */
export function refreshMarkers(b: LspBinding, path: string): void {
  const model = monaco.editor.getModel(modelUri(b.machineId, b.projectId, path));
  if (model) monaco.editor.setModelMarkers(model, OWNER, markersFor(path, b));
}

export { applyWorkspaceEdit };
